/**
 * The languages the interface itself ships in -- the single list behind the
 * header's language picker, Settings > Language, and the language step of
 * /complete-profile. Those three each carried their own hardcoded
 * `[{ value: 'en' }, { value: 'es' }]` array, which is how a fourth language
 * ends up half-added: the switcher offers it, the profile step doesn't.
 *
 * NOT the same list as HOST_LANGUAGE_OPTIONS (hostProfileOptions.ts), which
 * is the languages a host SPEAKS -- a much longer set that has nothing to do
 * with what the app is translated into.
 *
 * Labels are deliberately written in the language itself ("Slovenčina", not
 * "Slovak"): someone looking for their own language in a list they can't
 * currently read is scanning for the word they recognise.
 */
export interface AppLanguage {
  /** The i18next resource code -- must match a key in i18n.tsx's `resources`. */
  code: string;
  label: string;
}

export const APP_LANGUAGES: AppLanguage[] = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'sk', label: 'Slovenčina' },
];

/** Matches i18n.tsx's own `fallbackLng`. */
export const DEFAULT_APP_LANGUAGE = 'en';

/** Ready-shaped for antd's `Select`, which every one of the three pickers uses. */
export const APP_LANGUAGE_OPTIONS = APP_LANGUAGES.map(({ code, label }) => ({ value: code, label }));

/**
 * Narrows whatever i18next currently reports down to one of the codes above.
 *
 * It needs narrowing because the browser language detector hands back what
 * the browser actually says -- "sk-SK", "es-419", "en-GB" -- and i18next
 * keeps that exact string in `i18n.language` while resolving the translations
 * from the base code. So a `<Select value={i18n.language}>` against these
 * options matches nothing and renders the raw tag instead of the label.
 * Anything not on the list (a French browser, say) resolves to the fallback,
 * which is the language that visitor is really being shown.
 */
export const resolveAppLanguage = (raw?: string | null): string => {
  const base = (raw ?? '').split('-')[0].toLowerCase();
  return APP_LANGUAGES.some((language) => language.code === base) ? base : DEFAULT_APP_LANGUAGE;
};
